export interface Position {
  id: string;
  name: string;
  department: string;
  description: string;
  status: 'open' | 'closed' | 'paused';
  candidate_count?: number;
  is_public?: number;
  location?: string;
  salary_range?: string;
  requirements?: string;
  priority?: string;
  background?: string;
  funnel?: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface User {
  id: string;
  username: string;
  display_name: string;
  role: 'admin' | 'hr' | 'interviewer' | 'vip' | 'referrer' | 'it';
  status: 'active' | 'disabled' | 'pending';
  position_ids?: string[];
  permissions?: { modules: string[]; position_scope: 'all' | 'specific' };
  feishu_open_id?: string | null;
  phone?: string;
  created_at?: string;
}

export interface ReviewTask {
  id: string;
  candidate_id: string;
  interviewer_id: string;
  callback_token: string;
  status: 'pending' | 'completed' | 'expired';
  result?: 'passed' | 'rejected' | null;
  interviewer_name?: string;
  candidate_name?: string;
  created_at: string;
  updated_at: string;
}

export interface Evaluation {
  id: string;
  candidate_id: string;
  user_id?: string;
  interview_record_id?: string | null;
  author_name?: string;
  stage: string;
  rating: number;
  result: 'pending' | 'passed' | 'rejected';
  comment: string;
  is_private?: number;
  created_at: string;
}

export interface ActivityLog {
  id: string;
  candidate_id: string;
  action: string;
  detail: string;
  user_id?: string;
  operator_name?: string;
  created_at: string;
}

export interface Candidate {
  id: string;
  name: string;
  phone: string;
  email: string;
  wechat?: string;
  gender: string;
  age: number;
  photo_url: string;
  latest_company: string;
  latest_title: string;
  work_experiences?: { company: string; title: string }[];
  project_experiences?: string[];
  education: string;
  school: string;
  major: string;
  work_years: number;
  expected_salary: string;
  expected_city: string;
  resume_file: string;
  portfolio_url: string;
  personal_website: string;
  source: string;
  referred_by?: string;
  referrer_name?: string;
  tracking_code?: string;
  position_id: string | null;
  position_name?: string;
  stage: CandidateStage;
  status: 'pending' | 'passed' | 'rejected';
  notes: string;
  tags?: Tag[];
  evaluations?: Evaluation[];
  activity_logs?: ActivityLog[];
  resume_text?: string;
  resume_snapshots?: { id: string; source: string; created_at: string }[];
  jd_match_score?: number;
  jd_match_summary?: string;
  created_at: string;
  updated_at: string;
}

export type CandidateStage =
  | 'new'
  | 'screening'
  | 'screening_passed'
  | 'screening_rejected'
  | 'first_interview'
  | 'first_interview_rejected'
  | 'second_interview'
  | 'second_interview_rejected'
  | 'final_interview'
  | 'final_interview_rejected'
  | 'offer'
  | 'offer_rejected'
  | 'pending_onboard'
  | 'onboarded';

export const STAGE_LABELS: Record<CandidateStage, string> = {
  new: '待筛选',
  screening: '筛选中',
  screening_passed: '筛选通过',
  screening_rejected: '筛选淘汰',
  first_interview: '一面',
  first_interview_rejected: '一面淘汰',
  second_interview: '二面',
  second_interview_rejected: '二面淘汰',
  final_interview: '终面',
  final_interview_rejected: '终面淘汰',
  offer: 'Offer中',
  offer_rejected: '拒Offer',
  pending_onboard: '待入职',
  onboarded: '已入职',
};

export const PIPELINE_STAGES: CandidateStage[] = [
  'new',
  'screening',
  'screening_passed',
  'screening_rejected',
  'first_interview',
  'first_interview_rejected',
  'second_interview',
  'second_interview_rejected',
  'final_interview',
  'final_interview_rejected',
  'offer',
  'offer_rejected',
  'pending_onboard',
  'onboarded',
];

// 主流程阶段（不包含淘汰阶段），用于"通过"按钮的流转
export const MAIN_FLOW_STAGES: CandidateStage[] = [
  'new',
  'screening',
  'screening_passed',
  'first_interview',
  'second_interview',
  'final_interview',
  'offer',
  'pending_onboard',
  'onboarded',
];

// 用于UI展示的主流程阶段分组（带子分类）
export const DISPLAY_STAGE_GROUPS = [
  { 
    key: 'new', 
    label: 'HR筛选', 
    stages: ['new'],
    subCategories: null
  },
  { 
    key: 'screening', 
    label: '面试官筛选', 
    stages: ['screening', 'screening_passed', 'screening_rejected'],
    subCategories: [
      { key: 'screening', label: '待筛选', stage: 'screening' },
      { key: 'screening_passed', label: '筛选通过', stage: 'screening_passed' },
      { key: 'screening_rejected', label: '筛选淘汰', stage: 'screening_rejected' }
    ]
  },
  { 
    key: 'first_interview', 
    label: '一面', 
    stages: ['first_interview', 'first_interview_rejected'],
    subCategories: [
      { key: 'first_interview', label: '进行中', stage: 'first_interview' },
      { key: 'first_interview_rejected', label: '一面淘汰', stage: 'first_interview_rejected' }
    ]
  },
  { 
    key: 'second_interview', 
    label: '二面', 
    stages: ['second_interview', 'second_interview_rejected'],
    subCategories: [
      { key: 'second_interview', label: '进行中', stage: 'second_interview' },
      { key: 'second_interview_rejected', label: '二面淘汰', stage: 'second_interview_rejected' }
    ]
  },
  { 
    key: 'final_interview', 
    label: '终面', 
    stages: ['final_interview', 'final_interview_rejected'],
    subCategories: [
      { key: 'final_interview', label: '进行中', stage: 'final_interview' },
      { key: 'final_interview_rejected', label: '终面淘汰', stage: 'final_interview_rejected' }
    ]
  },
  { 
    key: 'offer', 
    label: 'Offer中', 
    stages: ['offer', 'offer_rejected'],
    subCategories: [
      { key: 'offer', label: 'Offer中', stage: 'offer' },
      { key: 'offer_rejected', label: '拒Offer', stage: 'offer_rejected' }
    ]
  },
  { 
    key: 'pending_onboard', 
    label: '待入职', 
    stages: ['pending_onboard'],
    subCategories: null
  },
  { 
    key: 'onboarded', 
    label: '已入职', 
    stages: ['onboarded'],
    subCategories: null
  },
];

export const EDUCATION_OPTIONS = [
  '高中', '大专', '本科', '硕士', '博士', '其他',
];

export const SOURCE_OPTIONS = [
  'Boss直聘', '拉勾', '猎聘', '智联招聘', '内推', '猎头', '官网', '其他',
];

// ====== 需求发起 ======
export type NeedStatus = 'debating' | 'discussing' | 'confirmed' | 'cancelled';
export type NeedUrgency = 'low' | 'normal' | 'high' | 'urgent';

export const URGENCY_LABELS: Record<NeedUrgency, string> = {
  low: '不急',
  normal: '正常',
  high: '紧急',
  urgent: '非常紧急',
};

export const NEED_STATUS_LABELS: Record<NeedStatus, string> = {
  debating: 'AI辩论中',
  discussing: '讨论中',
  confirmed: '已确认',
  cancelled: '已取消',
};

export interface HiringNeed {
  id: string;
  department: string;
  business_problem: string;
  headcount: number;
  urgency: NeedUrgency;
  status: NeedStatus;
  created_by: string;
  creator_name?: string;
  confirmed_by?: string;
  confirmed_at?: string;
  position_id?: string;
  need_source?: string;
  jd_title: string;
  jd_responsibilities: string;
  jd_requirements: string;
  first_interviewer_ids?: string[];
  second_interviewer_ids?: string[];
  comment_count?: number;
  member_count?: number;
  created_at: string;
  updated_at: string;
}

export interface NeedMember {
  need_id: string;
  user_id: string;
  role: 'creator' | 'collaborator';
  display_name: string;
  username: string;
  user_role: string;
  added_at: string;
}

export interface NeedDebate {
  id: string;
  need_id: string;
  side: 'pro' | 'con' | 'judge';
  content: string;
  created_at: string;
}

export type OrgUnitType = 'department' | 'team' | 'squad';

export type OrgBlueprintNodeType = 'company' | 'bu' | 'function' | 'department' | 'team' | 'project' | 'role' | 'squad';

export interface OrgUnit {
  id: string;
  name: string;
  type: OrgUnitType;
  parent_id?: string | null;
  parent_name?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  description: string;
  node_type?: OrgBlueprintNodeType | string;
  hc_plan?: number;
  hc_actual?: number;
  risk_level?: 'low' | 'medium' | 'high' | string;
  status: 'active' | 'archived';
  sort_order?: number;
  member_count?: number;
  key_person_count?: number;
  assignment_count?: number;
  recent_changed_at?: string | null;
  ideal_composition?: string;
  is_shadow?: number;
  linked_position_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface OrgMember {
  id: string;
  org_unit_id: string;
  org_unit_name?: string;
  user_id: string;
  display_name?: string;
  username?: string;
  role?: User['role'];
  title_in_team: string;
  is_key_person: number;
  is_manager: number;
  start_date?: string;
  end_date?: string;
  status: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
}

export interface OrgResponsibility {
  id: string;
  name: string;
  category: string;
  description: string;
  status: 'active' | 'archived';
  sort_order?: number;
  assignment_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface OrgAssignment {
  id: string;
  responsibility_id: string;
  responsibility_name?: string;
  responsibility_category?: string;
  org_unit_id: string;
  org_unit_name?: string;
  org_unit_type?: OrgUnitType;
  owner_user_id?: string | null;
  owner_name?: string | null;
  backup_user_id?: string | null;
  backup_name?: string | null;
  role_type: 'owner' | 'backup' | 'participant' | 'advisor';
  coverage_note: string;
  effective_from?: string;
  effective_to?: string;
  status: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
}

export interface OrgChangeLog {
  id: string;
  change_type: string;
  entity_type: string;
  entity_id: string;
  summary: string;
  before_json?: string;
  after_json?: string;
  changed_by?: string | null;
  changed_by_name?: string | null;
  changed_at: string;
}

export interface OrgOverviewResponse {
  units: OrgUnit[];
  members: OrgMember[];
  responsibilities: OrgResponsibility[];
  assignments: OrgAssignment[];
  stats: {
    unit_count: number;
    key_person_count: number;
    recent_change_count: number;
    single_point_risk_count: number;
    vacant_responsibility_count: number;
  };
}

export interface OrgBlueprintResponse {
  nodes: OrgUnit[];
}

export interface NeedComment {
  id: string;
  need_id: string;
  user_id: string;
  author_name: string;
  content: string;
  mentioned_user_ids: string;
  created_at: string;
}

export interface NeedJdHistory {
  id: string;
  need_id: string;
  user_id: string;
  editor_name: string;
  field: string;
  old_value: string;
  new_value: string;
  created_at: string;
}

export interface HiringNeedDetail extends HiringNeed {
  members: NeedMember[];
  debates: NeedDebate[];
  comments: NeedComment[];
  jd_history: NeedJdHistory[];
}

// ====== 人才中枢 ======
export interface Employee {
  id: string;
  name: string;
  avatar_url: string;
  employee_type: 'full_time' | 'intern' | 'outsource' | 'consultant';
  title: string;
  level: string;
  org_unit_id: string | null;
  user_id: string | null;
  candidate_id: string | null;
  core_output: string;
  workload: 'high' | 'medium' | 'low';
  monthly_cost: number;
  hire_date: string;
  is_leader?: number;
  resign_date?: string;
  recent_work_content?: { period: string; feishu_link?: string; report_summary?: string }[];
  status: 'active' | 'resigned' | 'on_leave';
  tags?: EmployeeTag[];
  created_at?: string;
  updated_at?: string;
}

export interface EmployeeTag {
  id: string;
  name: string;
  category?: 'skill' | 'experience' | 'certification';
  color: string;
}

export interface OrgSnapshot {
  id: string;
  name: string;
  snapshot_json?: any;
  created_by_name?: string;
  created_at: string;
}

export interface TalentRisk {
  type: 'critical' | 'warning' | 'info';
  category: 'vacancy' | 'overload' | 'orphan';
  unit_id: string;
  unit_name: string;
  message: string;
}

export interface TalentTreeResponse {
  units: OrgUnit[];
  employees: Employee[];
  unitStats: Record<string, { employee_count: number; total_monthly_cost: number; high_workload_count: number }>;
}

export const EMPLOYEE_TYPE_LABELS: Record<string, string> = {
  full_time: '正式',
  intern: '实习',
  outsource: '外包',
  consultant: '顾问',
};

export const WORKLOAD_LABELS: Record<string, string> = {
  high: '高负荷',
  medium: '正常',
  low: '低负荷',
};
